import { Discount } from "@medusajs/medusa"
import axios, { AxiosInstance } from "axios"
import { v4 as uuidv4 } from "uuid"

import { castToMarketPlaceStructure, MarketPlaceStructure } from "@modules/home/components/marketplaceHome/utils/structure"

export type SearchItemsParams = {
  searchString: string
  searchTypes: string[]
  categories: string[]
  collections: string[]
  pageNumber: number
  itemsPerPage: number
  minPrice?: number
  maxPrice?: number
  sortBy?: string
  inStock?: boolean
}

interface Icon {
  imageUrl: string
  backgoundImageUrl: string | null
  foregroundColorHex: string
  backgroundColorHex: string
  label: string
  backgroundAlpha: number
}

export enum LoanApplicationState {
  CREDIT_CHECK_ACCEPTED = "CREDIT_CHECK_ACCEPTED",
  CREDIT_CHECK_ACCEPTED_WB = "CREDIT_CHECK_ACCEPTED_WB",
  BELVO_SYNCED = "BELVO_SYNCED",
  CREDIT_CHECKED = "CREDIT_CHECKED",
  PRE_APPROVED = "PRE_APPROVED",
  PRE_APPROVED_TREATMENT_B = "PRE_APPROVED_TREATMENT_B",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  NEEDS_MANUAL_REVIEW = "NEEDS_MANUAL_REVIEW",
  SUBMITTED = "SUBMITTED",
  APPROVED_BY_MODEL = "APPROVED_BY_MODEL",
  APPROVED_DP_REQUIRED = "APPROVED_DP_REQUIRED",
}

export interface GetLoanApplicationInfoResponse {
  state: LoanApplicationState
  maxCreditLimit: number
}

export interface SearchProduct {
  name: string
  tags: string[]
  categories: string[]
  price: number
  reducedPrice: number | null
  icon: Icon
  type: string
  productGroupUuid: string
  productUrl: string
  badgeTypes: string[]
  discounts?: Discount[]
  asLowAsInstallment: number | null
  inStock: boolean | null
  countResponses: number | null
  averageRating: number | null
}

export interface GetLoanApplicationStateResponse {
  state: LoanApplicationState
}

export interface MexicoAddress {
  buildingNumber?: string
  street?: string
  interiorNumber?: string
  city?: string
  delegation?: string
  state?: string
  colony?: string
  postalCode?: string
  predetermined: boolean
  addressedTo?: string
  reference?: string
  deliveryInstructions?: string
  uuid?: string
}

export enum UserAddressRole {
  HOME = "HOME",
  DELIVERY = "DELIVERY",
}

export interface CreateAddressRequest {
  address: MexicoAddress
  role: UserAddressRole
}

export interface CreateAddressResponse {
  address: MexicoAddress
}

export interface AddressWrapper {
  priority: number
  address: MexicoAddress
  role: UserAddressRole
}

export interface GetAddressesResponse {
  addresses: AddressWrapper[]
  hasShippedBefore: boolean
}

interface PaymentOptionConfirmationContainer {
  description: string
  confirmationLabel: string
  cancellationLabel: string
}

export interface PaymentOption {
  uuid: string
  title: string
  subtitle: string
  tabInstallmentLabel: string
  summary?: string
  isUpfrontPaymentRequired?: boolean
  isAutopayRequired?: boolean
  installments: number
  confirmationContainer?: PaymentOptionConfirmationContainer
}

export interface PhoneNumber {
  number: string
  countryIso2: string
}

export interface VerifyCodeRequest {
  code: string
  phoneNumber: PhoneNumber
}

export type AuthAction = "SIGNUP" | "LOGIN" | "RESET_PIN" | "API" | "SET_PIN"

export interface AuthenticationResponse {
  token: string
  authAction: AuthAction
  userUuid?: string | null
}

export interface GetMarketplaceLoanPaymentOptionsResponse {
  paymentOptions: PaymentOption[]
  downPaymentDetails?: MarketplaceDownPaymentDetails
}

export interface MarketplaceDownPaymentDetails {
  minAmount: number
  maxAmount: number
}

export enum IDVerificationStatus {
  UNVERIFIED = "UNVERIFIED",
  SUBMITTED_VERIFICATION = "SUBMITTED_VERIFICATION",
  PROCESSING = "PROCESSING",
  VERIFIED = "VERIFIED",
  REVIEW_NEEDED = "REVIEW_NEEDED",
  REJECTED = "REJECTED",
  EXPIRED = "EXPIRED",
  DOCUMENT_MISMATCH = "DOCUMENT_MISMATCH_STATUS",
  FRAUD_REVIEW = "FRAUD_REVIEW",
}

interface WireMoney {
  amountE5: number
  currencyCode: string
}

export enum LoanOptionsRequestFlowType {
  SIGNUP = "signup",
  CHECKOUT = "checkout",
}

export interface LoanOptionsRequest {
  amount: WireMoney
  cartId: string
  flow?: LoanOptionsRequestFlowType
}

export interface CreateMarketplaceLoanRequest {
  cartId: string
  repaymentOptionUuid: string
  amount: WireMoney
  downPayment?: WireMoney
  forceNew?: boolean
}

export interface CreateMarketplaceLoanResponse {
  cartId: string
  loanInfo: LoanPreview
  isMarketplaceWebSignupPath: boolean
}

export interface AuthorizeLoanDownPaymentRequest {
  loanUuid: string
}

export interface AuthorizeLoanDownPaymentResponse {
  cardAuthorizationUuid: string
  secret: string
}

export interface LoanPreview {
  loanUuid: string
  breakdownLabel: string
  breakdown: BreakdownLineItem[]
  scheduleLabel: string
  schedule: BreakdownLineItem[]
  notes: string[]
}

interface BreakdownLineItem {
  labelTitle?: string
  overriddenLabelTitle?: string
  label: string
  strikeThroughLabel: boolean
  strikeThroughValue: boolean
  valueTitle?: string
  value: string
  bluePillText?: string
}

interface OnfidoApplicant {
  id: string
  createdAt: string
  sdk_token?: string
}

export interface ReferralData {
  source?: string
  medium?: string
  campaign?: string
  productId?: string | null
  variantId?: string | null
  isMarketplace?: boolean
  term?: string | null
  ad?: string | null
  content?: string | null
  utmId?: string | null
  url?: string | null
  gclid?: string | null
  checkoutType?: string | null
  pseudoId?: string | null
  productHandle?: string | null
  storefrontSessionId?: string | null
}

export interface PurchaseIntentRequest {
  email: string
  phone: string
  productId: string
  referralData: ReferralData
  productThumbnail: string
  productTitle: string
}

interface Config {
  baseUrl: string
  customHeaders?: Record<string, any>
}

export interface AddressDetail {
  postalCode: string
  colony: string
  municipality: string
  state: string
  city: string
}

export interface GetAddressesOfPostalCodeResponse {
  details: AddressDetail[]
}

export interface FingerprintDetails {
  requestId?: string
  visitorId?: string
  confidence?: any
  visitorFound?: boolean
  ip?: string
  ipLocation?: any
  os?: string
  osVersion?: string
  firstSeenAt?: any
  lastSeenAt?: any
}

export interface Address {
  countryIso2: string
  placeId: string | null
  addressMX?: MexicoAddress | null
  addressUS?: any
}

export interface DisplayAddress {
  line1: string
  line2: string
  line3: string
}

export interface UserProfileThumbnail {
  imageUrl: string | null
  backgoundImageUrl: string | null
  fullWidthBackgroundUrl: string | null
  foregroundColorHex: string
  backgroundColorHex: string
  label: string
  backgroundAlpha: number
}

export interface UserProfile {
  uuid: string
  displayName: string
  phoneNumber: string
  email: string
  displayAddress: DisplayAddress
  address: Address
  emailVerified: boolean
  thumbnail: UserProfileThumbnail
}

export interface GeoLocation {
  latitude: number
  longitude: number
  altitude?: number | null
}

export interface DeviceState {
  apps?: string | null
  location: GeoLocation | null
  carrier?: string | null
  fingerprintDetails?: FingerprintDetails
}

export interface TreatmentConfig<T> {
  treatment: string
  trackingKey: string
  config: T
}

export interface CreateCardRequest {
  cardToken: string
  lastFourDigits: string
  expirationMonth: string
  expirationYear: string
  brand: string
  loanUuid?: string
}

export interface CreateCardResponse {
  uuid: string
  lastFourDigits: string
  expirationMonth: string
  expirationYear: string
  brand: string
}

export interface ConfirmMarketplaceLoanDownPaymentRequest {
  cardUuid: string
  loanUuid: string
  completeCart?: boolean // if not set it's assumed to be false by the backend
}

export interface CardAuthorizationDetails {
  reference: string
  paymentIntentStatus: string
  cardAuthorizationId: string
  paymentIntentClientSecret?: string
}

export interface DisplayPaymentOptionsRequest {
  amount: WireMoney
}

export interface RetailDisplayPaymentOption {
  uuid: string
  title: string
  subtitle: string
  summary?: string
  installments: number
  footerNote?: string
}

export interface RetailDisplayPaymentOptionsResponse {
  retailDisplayPaymentOptions: RetailDisplayPaymentOption[]
}

export interface CheckIdentityVerificationStatusResponse {
  verificationStatus: IDVerificationStatus
}

export type SearchPagination = {
  totalCount: number
  currentPage: number
  totalPages?: number
  itemsPerPage?: number
  hasNextPage?: boolean
  hasPreviousPage: boolean
}

export type SearchResults = {
  result: SearchProduct[]
  pagination: SearchPagination
}

class NeloApiClient {
  private client: AxiosInstance
  private customHeaders: Record<string, any> = {}

  private static token: string | null = null

  constructor({ baseUrl, customHeaders }: Config) {
    this.customHeaders = customHeaders ?? {}

    this.client = axios.create({
      baseURL: baseUrl,
      timeout: 20_000,
      headers: {
        "Content-Type": "application/json",
      },
    })

    // Add a request interceptor to always use the latest token
    this.client.interceptors.request.use((config) => {
      const token = NeloApiClient.getToken()

      if (token && config.headers) {
        config.headers["Authorization"] = `Bearer ${token}`
      }
      return config
    })
  }

  /**
   * Sets the authentication token for client-side use.
   *
   * **NOTE**: This method only allows to set auth tokens in the browser environment.
   * When we run Next.js in server envs like Vercel the execution context could be shared between requests.
   * Storing tokens in a static variable on the server can lead to unintended token leakage and unexpected behavior
   * across requests.
   *
   * @param {string} token - The authentication token to be set.
   * @throws {Error} If called in a server-side environment.
   */
  static setToken(token: string) {
    if (typeof window == "undefined") {
      throw new Error("Can't set a static token server side")
    }

    this.token = token
  }

  static getToken(): string | null {
    return this.token
  }

  private getHeaders() {
    const defaultHeaders: Record<string, string> = {
      "Content-Type": "application/json",
      "Accept-Language": "es-MX",
      "x-device-timezone": "America/Mexico_City",
      "session-id": uuidv4(),
      "x-device-id": uuidv4(),
      "x-device-platform": "web",
      // todo: get these using FingerprintJS
      "x-device-manufacturer": "",
      "x-device-model": "",
    }

    const token = NeloApiClient.getToken()
    if (token) {
      defaultHeaders["Authorization"] = `Bearer ${token}`
    }

    return { ...defaultHeaders, ...this.customHeaders }
  }

  async searchItems(searchParams: SearchItemsParams): Promise<SearchResults> {
    try {
      const response = await this.client.post("/search/no-auth", searchParams)
      return { ...response.data, result: response.data.result || [] }
    } catch (error) {
      console.error("Error searching items:", error)
      throw error
    }
  }

  async getRecommendedProducts(searchParams: SearchItemsParams): Promise<{ result: SearchProduct[] }> {
    try {
      const response = await this.client.post(
        "/marketplace/get-recommended-products",
        {
          ...searchParams,
          recommender: "RECOMMENDED_FOR_YOU",
          categories: searchParams.categories.length > 0 ? searchParams.categories : null,
          collections: searchParams.collections.length > 0 ? searchParams.collections : null,
        },
        {
          headers: this.getHeaders(),
        }
      )

      return response.data
    } catch (error) {
      console.error("Error getting recommended products:", error)
      throw error
    }
  }

  async checkIdentityVerificationStatus(): Promise<CheckIdentityVerificationStatusResponse> {
    try {
      const response = await this.client.post(
        "/profile/identity-verification/status",
        {},
        {
          headers: this.getHeaders(),
        }
      )
      return response.data
    } catch (error) {
      console.error("Error getting identity verification status:", error)
      throw error
    }
  }

  async checkLoanApplicationStatus(): Promise<GetLoanApplicationInfoResponse> {
    try {
      const response = await this.client.get("/profile/loan-application/info", {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error getting loan application status:", error)
      throw error
    }
  }

  async getUserAddresses(): Promise<GetAddressesResponse> {
    try {
      const response = await this.client.get("/addresses", {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error getting loan options:", error)
      throw error
    }
  }

  async createAddress(request: CreateAddressRequest): Promise<CreateAddressResponse> {
    try {
      const response = await this.client.post("/address", request, {
        headers: this.getHeaders(),
      })
      console.log("Address created createAddress:", response.data)
      return response.data
    } catch (error) {
      console.error("Error creating address:", error)
      throw error
    }
  }

  async getLoanOptions(request: LoanOptionsRequest): Promise<GetMarketplaceLoanPaymentOptionsResponse> {
    try {
      const response = await this.client.post("/loan/in-app-bnpl/payment-options", request, { headers: this.getHeaders() })
      return response.data
    } catch (error) {
      console.error("Error getting loan options:", error)
      console.error("Error getting loan payment options:", error)
      throw error
    }
  }

  async createMarketplaceLoan(request: CreateMarketplaceLoanRequest): Promise<CreateMarketplaceLoanResponse> {
    try {
      const response = await this.client.post("/loan/in-app-bnpl", request, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error creating loan:", error)
      throw error
    }
  }

  async authorizeLoanDownPayment(request: AuthorizeLoanDownPaymentRequest): Promise<AuthorizeLoanDownPaymentResponse> {
    try {
      const response = await this.client.post("/loan/in-app-bnpl/authorize-down-payment", request, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error authorizing loan downpayment:", error)
      throw error
    }
  }

  async createOnfidoApplicant(): Promise<OnfidoApplicant> {
    try {
      const response = await this.client.post("/onfido/create-applicant", null, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error creating Onfido applicant:", error)
      throw error
    }
  }

  public async getHomePageStructure(): Promise<MarketPlaceStructure | null> {
    try {
      const response = await this.client.get("/marketplace/home/structure", {
        headers: this.getHeaders(),
      })

      const structured = castToMarketPlaceStructure(response.data)
      return structured
    } catch (error) {
      console.error("Error getting home page structure:", error)
      throw error
    }
  }

  async triggerOnfidoCheck(): Promise<void> {
    try {
      await this.client.post("/onfido/create-check", null, {
        headers: this.getHeaders(),
      })
    } catch (error) {
      console.error("Error triggering Onfido check:", error)
      throw error
    }
  }

  async createOnfidoLinkApplicant(): Promise<OnfidoApplicant> {
    try {
      const response = await this.client.post("/onfido-link/create-applicant", null, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error creating Onfido link applicant:", error)
      throw error
    }
  }

  async triggerOnfidoLinkCheck(): Promise<void> {
    try {
      await this.client.post("/onfido-link/create-check", null, {
        headers: this.getHeaders(),
      })
    } catch (error) {
      console.error("Error triggering Onfido link check:", error)
      throw error
    }
  }

  async createPurchaseIntent(request: PurchaseIntentRequest): Promise<void> {
    try {
      await this.client.post("/marketplace/purchase-intent", request, {
        headers: this.getHeaders(),
      })
    } catch (error) {
      console.error("Error creating purchase intent:", error)
      throw error
    }
  }

  async getAddressesOfPostalCode(postalCode: string): Promise<GetAddressesOfPostalCodeResponse | null> {
    try {
      const response = await this.client.get(`/postal-code-details?postalCode=${postalCode}`, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error checking postal code:", error)
      throw error
    }
  }

  async getExperimentTreatment<T>(experiment: string, attrs?: Record<string, any>): Promise<TreatmentConfig<T>> {
    try {
      const request = {
        experiment: experiment,
        attributes: attrs,
      }

      const response = await this.client.post(`/experimentation/get-treatment`, request, {
        headers: this.getHeaders(),
      })

      return response.data
    } catch (error) {
      console.error("Error getting experiment treatment:", error)
      throw error
    }
  }

  async verifyCode(request: VerifyCodeRequest): Promise<AuthenticationResponse> {
    try {
      const response = await this.client.put("/code/confirm", request, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error verifying code:", error)
      throw error
    }
  }

  async verifyUserCode(request: VerifyCodeRequest): Promise<AuthenticationResponse> {
    try {
      const response = await this.client.put("/code/user/confirm", request, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error verifying user code:", error)
      throw error
    }
  }

  async createCard(request: CreateCardRequest): Promise<CreateCardResponse> {
    try {
      const response = await this.client.post("/cards", request, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error creating card:", error)
      throw error
    }
  }

  async confirmMarketplaceLoanDownpayment(request: ConfirmMarketplaceLoanDownPaymentRequest): Promise<CardAuthorizationDetails> {
    try {
      const response = await this.client.post("/loan/in-app-bnpl/confirm-down-payment", request, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error confirming loan downpayment:", error)
      throw error
    }
  }

  async sendWhatsappVerificationLink(request?: string): Promise<void> {
    try {
      await this.client.post("/marketplace/send-onfido-link", request, {
        headers: this.getHeaders(),
      })
    } catch (error) {
      console.error("Error sending WhatsApp verification link:", error)
      throw error
    }
  }

  async getDisplayPaymentOptions(request: DisplayPaymentOptionsRequest): Promise<RetailDisplayPaymentOptionsResponse> {
    try {
      const response = await this.client.post("/marketplace/get-display-payment-options", request, {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error getting display payment options:", error)
      throw error
    }
  }

  async getUserProfile(): Promise<UserProfile> {
    try {
      const response = await this.client.get("/profile", {
        headers: this.getHeaders(),
      })
      return response.data
    } catch (error) {
      console.error("Error getting user profile:", error)
      throw error
    }
  }
}

export default NeloApiClient
